import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { CopyTableButtons } from './CopyTableButtons'
import { SpendCubeFaceCard } from './SpendCubeFace'
import { cell } from './copyTableToClipboard'
import {
  emptySummaryFilters,
  fetchSpendCubeSummary,
  formatRatio,
  formatSpend,
  formatSpendCompact,
  formatYoy,
  type SpendBuYear,
  type SpendCubeSummary,
  type SpendCubeViews,
  type SpendDashboard,
  type SpendCubeDim,
  type SpendSummaryFilters,
  type SpendYearAmount,
  type SpendYearRow,
} from './spendCubeApi'
import { EsBadge, riskBadgeTone } from './ui'

const EXCEL_BLUE = '#5B9BD5'
const CONCENTRATION_LINE = '#4472C4'
const EIGHTY_LINE = '#375DA1'
const PAIR_BLUE = '#0070C0'
const PAIR_GRAY = '#A5A5A5'

function yearKeys(rows: { years: SpendYearAmount[] }[]): number[] {
  return [...new Set(rows.flatMap((row) => row.years.map((y) => y.year)))].sort((a, b) => a - b)
}

function spendInYear(years: SpendYearAmount[], year: number): number {
  return years.find((y) => y.year === year)?.spend ?? 0
}

function Section({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-base font-bold text-es-navy">{title}</h2>
        {note ? <p className="text-sm text-es-muted mt-1">{note}</p> : null}
      </div>
      {children}
    </section>
  )
}

function bandLabel(band: string | null | undefined): string {
  if (band === 'high') return 'HIGH'
  if (band === 'medium') return 'MEDIUM'
  if (band === 'low') return 'LOW'
  return 'UNSCORED'
}

function QuietStat({ label, value, valueClassName = 'text-es-navy' }: { label: string; value: string; valueClassName?: string }) {
  return (
    <div className="rounded-[var(--radius-md)] border border-es-soft bg-es-haze/50 px-3 py-2">
      <div className={`text-base font-bold tabular-nums ${valueClassName}`}>{value}</div>
      <div className="text-[10px] font-bold uppercase tracking-wider text-es-muted mt-1">{label}</div>
    </div>
  )
}

function YearColumns({ rows }: { rows: { label: string; value: number }[] }) {
  const max = Math.max(1, ...rows.map((row) => row.value))
  if (rows.length === 0) return <p className="text-sm text-es-muted">No invoice years in this filter.</p>
  return (
    <div className="flex items-end gap-6 h-36 w-full max-w-xs">
      {rows.map((row) => (
        <div key={row.label} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
          <span className="text-xs tabular-nums text-es-muted mb-1">{formatSpendCompact(row.value)}</span>
          <div
            className="w-full max-w-14 rounded-t-sm"
            style={{ height: `${Math.max(8, (row.value / max) * 100)}%`, backgroundColor: EXCEL_BLUE }}
          />
          <span className="text-xs font-medium text-es-navy mt-2">{row.label}</span>
        </div>
      ))}
    </div>
  )
}

function UtilizationMeter({
  title,
  ratio,
  fillLabel,
  fillValue,
  trackLabel,
  trackValue,
  note,
}: {
  title: string
  ratio: number | null
  fillLabel: string
  fillValue: number
  trackLabel: string
  trackValue: number
  note?: string
}) {
  const width = ratio == null ? 0 : Math.max(0, Math.min(100, ratio * 100))
  return (
    <section className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4 md:p-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <h3 className="text-sm font-bold text-es-navy uppercase tracking-wider">{title}</h3>
        <div className="text-3xl font-bold tabular-nums text-es-navy">{formatRatio(ratio)}</div>
      </div>
      <div className="mt-4 h-4 rounded-full bg-es-haze overflow-hidden" aria-hidden>
        <div className="h-full rounded-full" style={{ width: `${width}%`, backgroundColor: EXCEL_BLUE }} />
      </div>
      <div className="mt-2 flex flex-wrap justify-between gap-2 text-xs text-es-navy">
        <span>
          <span className="text-es-muted">{fillLabel} </span>
          <span className="font-semibold tabular-nums">{formatSpend(fillValue)}</span>
        </span>
        <span>
          <span className="text-es-muted">{trackLabel} </span>
          <span className="font-semibold tabular-nums">{formatSpend(trackValue)}</span>
        </span>
      </div>
      {note ? <p className="text-xs text-es-muted mt-2">{note}</p> : null}
    </section>
  )
}

function DataTable({
  title,
  headers,
  rows,
  footer,
  canOpenInCube,
  onOpenInCube,
}: {
  title: string
  headers: string[]
  rows: string[][]
  footer?: string[]
  canOpenInCube?: (rowIndex: number) => boolean
  onOpenInCube?: (rowIndex: number) => void
}) {
  return (
    <section className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-bold text-es-navy uppercase tracking-wider">{title}</h3>
        <CopyTableButtons payload={{ headers, rows }} />
      </div>
      <div className="overflow-x-auto max-h-[28rem]">
        <table className="w-full text-left text-sm border-collapse min-w-[28rem]">
          <thead className="sticky top-0 bg-es-haze">
            <tr>
              {headers.map((h) => (
                <th key={h} className="px-2 py-2 font-semibold uppercase tracking-wider text-xs text-es-muted border-b border-es-soft">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={headers.length || 1} className="px-2 py-4 text-es-muted text-center">
                  No rows for these filters.
                </td>
              </tr>
            ) : (
              rows.map((row, i) => (
                <tr key={i} className="border-b border-es-soft/60">
                  {row.map((value, j) => (
                    <td key={j} className={`px-2 py-2 ${j === 0 ? 'font-medium text-es-navy' : 'tabular-nums'}`}>
                      {j === 0 && onOpenInCube && canOpenInCube?.(i) ? (
                        <span className="inline-flex flex-wrap items-center gap-2">
                          <span>{value}</span>
                          <button
                            type="button"
                            className="text-xs font-semibold text-es-signal hover:underline"
                            onClick={() => onOpenInCube(i)}
                          >
                            Open in cube
                          </button>
                        </span>
                      ) : (
                        value
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
          {footer && footer.length > 0 ? (
            <tfoot>
              <tr className="border-t border-es-soft bg-es-haze">
                {footer.map((value, j) => (
                  <td key={j} className={`px-2 py-2 font-semibold ${j === 0 ? 'text-es-navy' : 'tabular-nums'}`}>
                    {value}
                  </td>
                ))}
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>
    </section>
  )
}

function yearTableRows(rows: SpendYearRow[], years: number[]): string[][] {
  return rows.map((row) => [
    cell(row.key),
    ...years.map((year) => formatSpend(spendInYear(row.years, year))),
    formatSpend(row.spend),
  ])
}

function yearTotalsFooter(
  labelCount: number,
  years: number[],
  rows: { years: SpendYearAmount[]; spend: number }[]
): string[] | undefined {
  if (rows.length === 0) return undefined
  const labels = Array.from({ length: labelCount }, (_, index) => (index === 0 ? 'Grand total' : ''))
  return [
    ...labels,
    ...years.map((year) => formatSpend(rows.reduce((sum, row) => sum + spendInYear(row.years, year), 0))),
    formatSpend(rows.reduce((sum, row) => sum + row.spend, 0)),
  ]
}

function buTotalsFooter(siteHeaders: string[], rows: SpendBuYear[]): string[] | undefined {
  if (rows.length === 0) return undefined
  return [
    'Grand total',
    ...siteHeaders.map((site) =>
      formatSpend(rows.reduce((sum, row) => sum + (row.sites.find((item) => item.key === site)?.spend ?? 0), 0))
    ),
    formatSpend(rows.reduce((sum, row) => sum + row.spend, 0)),
  ]
}

function ChartCard({ title, note, children }: { title: string; note?: string; children: ReactNode }) {
  return (
    <section className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4">
      <h3 className="text-sm font-bold text-es-navy uppercase tracking-wider">{title}</h3>
      {note ? <p className="text-xs text-es-muted mt-1 mb-3">{note}</p> : <div className="mb-3" />}
      {children}
    </section>
  )
}

function ColumnChart({
  title,
  note,
  rows,
}: {
  title: string
  note?: string
  rows: { label: string; value: number }[]
}) {
  const max = Math.max(1, ...rows.map((row) => row.value))
  return (
    <ChartCard title={title} note={note}>
      {rows.length === 0 ? (
        <p className="text-sm text-es-muted">No invoice years in this filter.</p>
      ) : (
        <div className="flex items-end gap-8 h-52 px-4">
          {rows.map((row) => (
            <div key={row.label} className="flex-1 flex flex-col items-center justify-end h-full min-w-0">
              <span className="text-xs tabular-nums text-es-muted mb-1">{formatSpendCompact(row.value)}</span>
              <div
                className="w-full max-w-16 rounded-t-sm"
                style={{ height: `${Math.max(6, (row.value / max) * 78)}%`, backgroundColor: EXCEL_BLUE }}
              />
              <span className="text-xs font-medium text-es-navy mt-2">{row.label}</span>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  )
}

function PairedColumns({
  title,
  note,
  aLabel,
  bLabel,
  aColor,
  bColor,
  rows,
}: {
  title: string
  note?: string
  aLabel: string
  bLabel: string
  aColor: string
  bColor: string
  rows: { label: string; a: number; b: number }[]
}) {
  const max = Math.max(1, ...rows.flatMap((row) => [row.a, row.b]))
  return (
    <ChartCard title={title} note={note}>
      <div className="flex gap-4 text-xs text-es-muted mb-3">
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm" style={{ backgroundColor: aColor }} />{aLabel}</span>
        <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-sm" style={{ backgroundColor: bColor }} />{bLabel}</span>
      </div>
      <div className="flex items-end gap-2 h-56 overflow-x-auto">
        {rows.map((row) => (
          <div key={row.label} className="flex-1 min-w-[4.5rem] flex flex-col items-center justify-end h-full">
            <div className="flex items-end justify-center gap-1 w-full h-[calc(100%-2.5rem)]">
              <div className="w-3 rounded-t-sm" style={{ height: `${Math.max(4, (row.a / max) * 100)}%`, backgroundColor: aColor }} title={`${aLabel} ${formatSpend(row.a)}`} />
              <div className="w-3 rounded-t-sm" style={{ height: `${Math.max(4, (row.b / max) * 100)}%`, backgroundColor: bColor }} title={`${bLabel} ${formatSpend(row.b)}`} />
            </div>
            <span className="text-[10px] leading-tight text-center text-es-navy mt-2">{row.label}</span>
          </div>
        ))}
      </div>
    </ChartCard>
  )
}

function ShareMosaic({
  items,
}: {
  items: { key: string; share: number | null }[]
}) {
  const total = items.reduce((sum, item) => sum + (item.share ?? 0), 0) || 1
  return (
    <ChartCard title="Top 15 SAP items as % of spend" note="Share of 2024–2025 invoice spend.">
      {items.length === 0 ? (
        <p className="text-sm text-es-muted">No SAP items in this filter.</p>
      ) : (
        <div className="flex flex-wrap gap-1 content-start min-h-52">
          {items.map((item) => (
            <div
              key={item.key}
              className="bg-es-navy/10 border border-es-soft rounded-[var(--radius-sm)] p-2 min-w-[6.5rem]"
              style={{ flexGrow: Math.max(item.share ?? 0, total / 40), flexBasis: `${((item.share ?? 0) / total) * 100}%` }}
            >
              <div className="text-xs font-medium text-es-navy leading-snug">{item.key}</div>
              <div className="text-sm font-bold tabular-nums text-es-navy">{formatRatio(item.share)}</div>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  )
}

function ConcentrationChart({
  points,
  tailRank,
}: {
  points: { rank: number; cumulativeShare: number | null }[]
  tailRank: number | null
}) {
  const width = 640
  const height = 320
  const plot = { left: 44, right: 16, top: 16, bottom: 36 }
  const plotW = width - plot.left - plot.right
  const plotH = height - plot.top - plot.bottom
  const lastRank = points.length || 1
  const xy = (rank: number, share: number) => ({
    x: plot.left + ((rank - 1) / Math.max(lastRank - 1, 1)) * plotW,
    y: plot.top + (1 - share) * plotH,
  })
  const coords = points
    .map((point) => {
      const p = xy(point.rank, point.cumulativeShare ?? 0)
      return `${p.x.toFixed(1)},${p.y.toFixed(1)}`
    })
    .join(' ')
  const y80 = xy(1, 0.8).y
  const crossing = tailRank != null ? points.find((point) => point.rank === tailRank) : null
  const mark = crossing ? xy(crossing.rank, crossing.cumulativeShare ?? 0.8) : null
  const yTicks = [0, 0.2, 0.4, 0.6, 0.8, 1]
  const xTicks = [...new Set([1, tailRank, lastRank].filter((rank): rank is number => rank != null && rank >= 1 && rank <= lastRank))]
  const caption =
    tailRank != null
      ? `${tailRank} of ${points.length} suppliers reach 80% of invoice spend.`
      : 'Cumulative spend share by supplier rank.'
  return (
    <ChartCard title="Supplier concentration and tail spend" note={caption}>
      {points.length === 0 ? (
        <p className="text-sm text-es-muted">No suppliers in this filter.</p>
      ) : (
        <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-72" role="img" aria-label={caption}>
          {yTicks.map((tick) => {
            const y = xy(1, tick).y
            return (
              <g key={tick}>
                <line x1={plot.left} y1={y} x2={width - plot.right} y2={y} className="text-es-soft" stroke="currentColor" />
                <text x={plot.left - 8} y={y + 4} textAnchor="end" className="fill-current text-es-muted text-[11px]">
                  {Math.round(tick * 100)}%
                </text>
              </g>
            )
          })}
          <line x1={plot.left} y1={y80} x2={width - plot.right} y2={y80} stroke={EIGHTY_LINE} strokeDasharray="6 4" strokeWidth="1.5" />
          <polyline fill="none" stroke={CONCENTRATION_LINE} strokeWidth="2.5" points={coords} />
          {mark ? (
            <>
              <circle cx={mark.x} cy={mark.y} r="5" fill={EIGHTY_LINE} />
              <text x={mark.x + 8} y={mark.y - 8} className="fill-current text-es-navy text-[12px] font-semibold">
                Rank {tailRank}
              </text>
            </>
          ) : null}
          {xTicks.map((rank) => {
            const x = xy(rank, 0).x
            return (
              <text key={rank} x={x} y={height - 10} textAnchor="middle" className="fill-current text-es-muted text-[11px]">
                {rank}
              </text>
            )
          })}
        </svg>
      )}
    </ChartCard>
  )
}

function HorizontalBars({
  title,
  rows,
  onRowClick,
  onOpenInCube,
}: {
  title: string
  rows: { key: string; spend: number }[]
  onRowClick?: (key: string) => void
  onOpenInCube?: (key: string) => void
}) {
  const max = Math.max(1, ...rows.map((row) => row.spend))
  return (
    <ChartCard title={title}>
      {rows.length === 0 ? (
        <p className="text-sm text-es-muted">No rows in this filter.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => {
            const bar = (
              <>
                <div className="flex items-baseline justify-between gap-3 text-xs mb-1">
                  <span className="font-medium text-es-navy truncate" title={row.key}>
                    {row.key}
                  </span>
                  <span className="tabular-nums text-es-navy shrink-0">{formatSpendCompact(row.spend)}</span>
                </div>
                <div className="h-5 rounded-sm bg-es-haze overflow-hidden">
                  <div
                    className="h-full rounded-sm"
                    style={{ width: `${Math.max(2, (row.spend / max) * 100)}%`, backgroundColor: EXCEL_BLUE }}
                  />
                </div>
              </>
            )
            const canCube = onOpenInCube && row.key !== 'Unmapped'
            return (
              <li key={row.key}>
                {onRowClick ? (
                  <button
                    type="button"
                    className="w-full text-left rounded-[var(--radius-sm)] hover:bg-es-haze/60 focus:outline-none focus:ring-1 focus:ring-es-signal"
                    onClick={() => onRowClick(row.key)}
                  >
                    {bar}
                  </button>
                ) : (
                  bar
                )}
                {canCube ? (
                  <button
                    type="button"
                    className="mt-1 text-xs font-semibold text-es-signal hover:underline"
                    onClick={() => onOpenInCube(row.key)}
                  >
                    Open in cube
                  </button>
                ) : null}
              </li>
            )
          })}
        </ul>
      )}
    </ChartCard>
  )
}

function Slicer({
  label,
  options,
  selected,
  onChange,
  searchable,
  open,
  onToggle,
}: {
  label: string
  options: string[]
  selected: string[]
  onChange: (next: string[]) => void
  searchable?: boolean
  open: boolean
  onToggle: () => void
}) {
  const [query, setQuery] = useState('')
  const selectedSet = new Set(selected)
  const q = query.trim().toLowerCase()
  const matched = q ? options.filter((option) => option.toLowerCase().includes(q)) : options
  const clipped = searchable && !q && matched.length > 80
  const visible = clipped
    ? [...selected.filter((value) => options.includes(value)), ...matched.filter((value) => !selectedSet.has(value)).slice(0, 40)]
    : matched
  return (
    <div className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-label={label}
        onClick={onToggle}
        className={`rounded-[var(--radius-sm)] border px-2.5 py-1.5 text-xs font-semibold ${
          selected.length
            ? 'border-[#5B9BD5] bg-[#5B9BD5]/15 text-es-navy'
            : 'border-es-soft bg-es-white text-es-navy hover:border-[#5B9BD5]'
        }`}
      >
        {selected.length ? `${label} (${selected.length})` : label}
      </button>
      {open ? (
        <div className="absolute left-0 top-full z-20 mt-1 w-64 rounded-[var(--radius-md)] border border-es-soft bg-es-white shadow-[var(--shadow-card)] p-2">
          {searchable ? (
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search"
              aria-label={`Search ${label}`}
              className="mb-2 w-full rounded-[var(--radius-sm)] border border-es-soft px-2 py-1 text-sm"
            />
          ) : null}
          <div className="max-h-52 overflow-y-auto space-y-1">
            {visible.length === 0 ? (
              <p className="px-1 py-2 text-xs text-es-muted">No matches.</p>
            ) : (
              visible.map((option) => (
                <label key={option} className="flex items-center gap-2 px-1 py-0.5 text-sm text-es-navy">
                  <input
                    type="checkbox"
                    checked={selectedSet.has(option)}
                    onChange={() =>
                      onChange(selectedSet.has(option) ? selected.filter((value) => value !== option) : [...selected, option])
                    }
                  />
                  <span className="truncate" title={option}>
                    {option}
                  </span>
                </label>
              ))
            )}
          </div>
          {clipped ? (
            <p className="mt-2 text-[11px] text-es-muted">{matched.length} values. Type to search.</p>
          ) : null}
          {selected.length > 0 ? (
            <button type="button" onClick={() => onChange([])} className="mt-2 text-xs font-semibold text-es-signal hover:underline">
              Clear
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function tableForFace(rowDim: SpendCubeDim): string | null {
  switch (rowDim) {
    case 'parent':
      return 'supplier'
    case 'rollup':
      return 'rollup'
    case 'year':
      return 'year'
    case 'bu':
      return 'bu'
    case 'plant':
      return 'plant'
    case 'material':
      return 'material'
    case 'status':
      return 'status'
    case 'contractor':
      return 'contractor'
    default:
      return null
  }
}

function SummaryTables({ summary, rowDim }: { summary: SpendCubeSummary; rowDim: SpendCubeDim }) {
  const [showTables, setShowTables] = useState(false)
  const supplierYears = yearKeys(summary.suppliersByYear)
  const statusYears = yearKeys(summary.statusByYear)
  const contractorYears = yearKeys(summary.contractorByYear)
  const rollupYears = yearKeys(summary.reportRollups)
  const poYears = yearKeys(summary.topPoLines)
  const materialYears = yearKeys(summary.materialByYear)
  const plantYears = yearKeys(summary.plantByYear)
  const siteHeaders = [...new Set(summary.buByYear.flatMap((row) => row.sites.map((site) => site.key)))]
  const match = tableForFace(rowDim)
  useEffect(() => {
    setShowTables(false)
  }, [rowDim])
  const tables: { id: string; node: ReactNode }[] = [
    {
      id: 'yearChart',
      node: (
      <ColumnChart
        title="Spend trend by year"
        rows={summary.byYear.map((row) => ({ label: String(row.year), value: row.spend }))}
      />
      ),
    },
    {
      id: 'year',
      node: (
      <DataTable
        title="Spend trend by year"
        headers={['Invoice year', 'Spend']}
        rows={summary.byYear.map((row) => [String(row.year), formatSpend(row.spend)])}
      />
      ),
    },
    {
      id: 'supplier',
      node: (
      <DataTable
        title="Spend trend by supplier"
        headers={['Supplier', ...supplierYears.map(String), 'Grand total']}
        rows={summary.suppliersByYear.map((row) => [
          cell(row.parentName),
          ...supplierYears.map((year) => formatSpend(spendInYear(row.years, year))),
          formatSpend(row.spend),
        ])}
        footer={yearTotalsFooter(1, supplierYears, summary.suppliersByYear)}
      />
      ),
    },
    {
      id: 'status',
      node: (
      <DataTable
        title="Spend trend by line status"
        headers={['Line status', ...statusYears.map(String), 'Grand total']}
        rows={yearTableRows(summary.statusByYear, statusYears)}
        footer={yearTotalsFooter(1, statusYears, summary.statusByYear)}
      />
      ),
    },
    {
      id: 'contractor',
      node: (
      <DataTable
        title="Contractor / consultant classification"
        headers={['Contractor / consultant', ...contractorYears.map(String), 'Grand total']}
        rows={yearTableRows(summary.contractorByYear, contractorYears)}
        footer={yearTotalsFooter(1, contractorYears, summary.contractorByYear)}
      />
      ),
    },
    {
      id: 'bu',
      node: (
      <DataTable
        title="Spend breakdown by BU"
        headers={['Invoice year', ...siteHeaders, 'Grand total']}
        rows={summary.buByYear.map((row) => [
          String(row.year),
          ...siteHeaders.map((site) => formatSpend(row.sites.find((item) => item.key === site)?.spend ?? 0)),
          formatSpend(row.spend),
        ])}
        footer={buTotalsFooter(siteHeaders, summary.buByYear)}
      />
      ),
    },
    {
      id: 'rollup',
      node: (
      <DataTable
        title="Report rollup and roll up 2"
        headers={['Report rollup', 'Roll up 2', ...rollupYears.map(String), 'Grand total']}
        rows={summary.reportRollups.map((row) => [
          cell(row.reportRollup),
          cell(row.rollup2),
          ...rollupYears.map((year) => formatSpend(spendInYear(row.years, year))),
          formatSpend(row.spend),
        ])}
        footer={yearTotalsFooter(2, rollupYears, summary.reportRollups)}
      />
      ),
    },
    {
      id: 'po',
      node: (
      <DataTable
        title="Top 10 purchase orders and line description"
        headers={['Purchase order', 'Description', ...poYears.map(String), 'Grand total']}
        rows={summary.topPoLines.map((row) => [
          cell(row.poNumber),
          cell(row.description),
          ...poYears.map((year) => formatSpend(spendInYear(row.years, year))),
          formatSpend(row.spend),
        ])}
        footer={yearTotalsFooter(2, poYears, summary.topPoLines)}
      />
      ),
    },
    {
      id: 'material',
      node: (
      <DataTable
        title="Spend breakdown by material group"
        headers={['Material group', ...materialYears.map(String), 'Grand total']}
        rows={yearTableRows(summary.materialByYear, materialYears)}
        footer={yearTotalsFooter(1, materialYears, summary.materialByYear)}
      />
      ),
    },
    {
      id: 'plant',
      node: (
      <DataTable
        title="Spend breakdown by plant location"
        headers={['Plant location', ...plantYears.map(String), 'Grand total']}
        rows={yearTableRows(summary.plantByYear, plantYears)}
        footer={yearTotalsFooter(1, plantYears, summary.plantByYear)}
      />
      ),
    },
  ]
  const featured = tables.find((table) => table.id === match)
  const rest = tables.filter((table) => table.id !== match)
  return (
    <div className="space-y-4">
      {featured ? featured.node : <p className="text-sm text-es-muted">This face has no matching workbook table.</p>}
      <button
        type="button"
        aria-expanded={showTables}
        onClick={() => setShowTables((open) => !open)}
        className="text-sm font-semibold text-es-signal hover:underline"
      >
        {showTables ? 'Hide the tables' : 'Show the tables'}
      </button>
      {showTables ? rest.map((table) => <div key={table.id}>{table.node}</div>) : null}
    </div>
  )
}

export function SpendWorkbookPanels({
  dashboard,
  views,
  companyId,
  pageYear,
  pageBu,
  pageRollup,
  onSupplier,
  onCategory,
  drill,
  drillFocus,
  bindOpenInCube,
}: {
  dashboard: SpendDashboard
  views: SpendCubeViews | null
  companyId: string
  pageYear: number | null
  pageBu: string
  pageRollup: string
  onSupplier: (name: string) => void
  onCategory: (name: string) => void
  drill?: ReactNode
  drillFocus?: string
  bindOpenInCube?: { current: (slice: { parent?: string; rollup?: string }) => void }
}) {
  const [sheet, setSheet] = useState<'executive' | 'po' | 'summary'>('executive')
  const [cubeRowDim, setCubeRowDim] = useState<SpendCubeDim>('parent')
  const onCubeRowDim = useCallback((dim: SpendCubeDim) => setCubeRowDim(dim), [])
  const drillRef = useRef<HTMLDivElement>(null)
  const [summaryFilters, setSummaryFilters] = useState<SpendSummaryFilters>(() =>
    emptySummaryFilters({ year: pageYear, bu: pageBu, rollup: pageRollup })
  )
  const [summary, setSummary] = useState<SpendCubeSummary | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(false)
  const [summaryError, setSummaryError] = useState<string | null>(null)
  const [openSlicer, setOpenSlicer] = useState<string | null>(null)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const slicerBarRef = useRef<HTMLDivElement>(null)
  const yearAmounts = views?.byYear.length ? views.byYear : dashboard.byYear
  const supplierCount = views?.activeSuppliers || dashboard.activeSuppliers
  const categoryBars = [...dashboard.byCategory]
    .map((row) => ({ key: row.key === 'Unclassified' ? 'Unmapped' : row.key, spend: row.spend }))
    .sort((a, b) => b.spend - a.spend || a.key.localeCompare(b.key))
  const invoiceParents = new Set((views?.concentration || []).map((point) => point.parentName))

  const openInCube = (slice: { parent?: string; rollup?: string }) => {
    const next = emptySummaryFilters({
      year: pageYear,
      bu: pageBu,
      rollup: slice.rollup ?? pageRollup,
    })
    if (slice.parent) next.parents = [slice.parent]
    setSummaryFilters(next)
    setFiltersOpen(false)
    setOpenSlicer(null)
    setSheet('summary')
  }
  if (bindOpenInCube) bindOpenInCube.current = openInCube

  useEffect(() => {
    if (!drillFocus) return
    drillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
  }, [drillFocus])

  useEffect(() => {
    setSummaryFilters((prev) => {
      const years = pageYear ? [pageYear] : []
      const bus = pageBu ? [pageBu] : []
      const rollups = pageRollup ? [pageRollup] : []
      const sameYears = prev.years.length === years.length && prev.years.every((value, index) => value === years[index])
      const sameBu = prev.bus.length === bus.length && prev.bus[0] === bus[0]
      const sameRollup = prev.rollups.length === rollups.length && prev.rollups[0] === rollups[0]
      if (sameYears && sameBu && sameRollup) return prev
      return { ...prev, years, bus, rollups }
    })
  }, [pageYear, pageBu, pageRollup])

  useEffect(() => {
    if (!openSlicer) return
    const close = (event: MouseEvent) => {
      if (slicerBarRef.current && !slicerBarRef.current.contains(event.target as Node)) setOpenSlicer(null)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [openSlicer])

  useEffect(() => {
    if (sheet !== 'summary' || !companyId) return
    let cancel = false
    setSummaryLoading(true)
    setSummaryError(null)
    fetchSpendCubeSummary(companyId, summaryFilters)
      .then((data) => {
        if (!cancel) setSummary(data)
      })
      .catch((error: unknown) => {
        if (!cancel) setSummaryError(error instanceof Error ? error.message : 'Could not load the summary.')
      })
      .finally(() => {
        if (!cancel) setSummaryLoading(false)
      })
    return () => {
      cancel = true
    }
  }, [sheet, companyId, summaryFilters])

  const sheets = [
    { id: 'executive' as const, label: 'Executive Dashboard' },
    { id: 'po' as const, label: 'PO & Budget Utilisation' },
    { id: 'summary' as const, label: 'Spend Cube Summary' },
  ]

  return (
    <div className="space-y-4">
      <div role="tablist" aria-label="Spend workbook sheets" className="flex flex-wrap gap-2">
        {sheets.map((tab) => {
          const selected = sheet === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={selected}
              onClick={() => setSheet(tab.id)}
              className={`rounded-[var(--radius-sm)] px-3 py-2 text-sm font-semibold border ${
                selected
                  ? 'bg-es-navy text-white border-es-navy'
                  : 'bg-es-white text-es-navy border-es-soft hover:border-es-navy'
              }`}
            >
              {tab.label}
            </button>
          )
        })}
      </div>

      {sheet === 'executive' ? (
      <div className="space-y-4">
        <section className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4 md:p-5">
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-es-muted">Invoice spend</p>
              <p className="text-4xl font-bold tabular-nums text-es-navy mt-1">{formatSpend(dashboard.totalSpend)}</p>
              <p className="text-xs text-es-muted mt-2">2024–2025 invoice spend, all suppliers.</p>
            </div>
            <YearColumns rows={yearAmounts.map((row) => ({ label: String(row.year), value: row.spend }))} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            <QuietStat label="Latest-year growth" value={formatYoy(dashboard.yoy)} />
            <QuietStat label="Active suppliers" value={String(supplierCount)} />
            <QuietStat label="Top 10 concentration" value={formatRatio(dashboard.top10Concentration)} />
            <QuietStat label="Consultant spend" value={formatRatio(dashboard.consultantShare)} />
          </div>
        </section>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <HorizontalBars
            title="Spend mix by category"
            rows={categoryBars}
            onRowClick={(key) => {
              if (key !== 'Unmapped') onCategory(key)
            }}
            onOpenInCube={(key) => openInCube({ rollup: key })}
          />
          <HorizontalBars
            title="Top 15 suppliers"
            rows={(views?.topSuppliers || []).map((row) => ({ key: row.key, spend: row.spend }))}
            onRowClick={onSupplier}
            onOpenInCube={(key) => openInCube({ parent: key })}
          />
        </div>
        {drill ? (
          <div ref={drillRef} className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4 md:p-5">
            {drill}
          </div>
        ) : null}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          <ConcentrationChart points={views?.concentration || []} tailRank={views?.tailRank ?? null} />
          <ShareMosaic items={views?.bySap || []} />
          <HorizontalBars
            title="Plant-wise spend"
            rows={[...(views?.byPlant || [])]
              .map((row) => ({ key: row.key, spend: row.spend }))
              .sort((a, b) => b.spend - a.spend || a.key.localeCompare(b.key))}
          />
        </div>
        <section className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4 md:p-5">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <QuietStat label="Spend on high risk" value={formatSpend(dashboard.highRiskSpend)} valueClassName="text-es-riskHigh" />
            <QuietStat label="Single-source spend" value={formatSpend(dashboard.singleSourceSpend)} />
            <QuietStat label="Open mitigations" value={String(dashboard.openMitigations)} />
          </div>
          {dashboard.insights[0]?.text ? <p className="text-sm text-es-navy mt-4">{dashboard.insights[0].text}</p> : null}
        </section>
        <section className="bg-es-white border border-es-soft shadow-[var(--shadow-card)] rounded-[var(--radius-lg)] p-4">
          <div className="flex items-center justify-between gap-2 mb-3">
            <h3 className="text-sm font-bold text-es-navy uppercase tracking-wider">Top suppliers</h3>
            <CopyTableButtons
              payload={{
                headers: ['Supplier', 'Spend', 'Share', 'Risk', 'Priority'],
                rows: dashboard.topSuppliers.map((row) => [
                  cell(row.parentName),
                  formatSpend(row.spend),
                  formatRatio(row.share),
                  bandLabel(row.band),
                  row.priorityLine || '',
                ]),
              }}
            />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse min-w-[36rem]">
              <thead className="sticky top-0 bg-es-haze">
                <tr>
                  {['Supplier', 'Spend', 'Share', 'Risk', 'Priority'].map((header) => (
                    <th key={header} className="px-2 py-2 font-semibold uppercase tracking-wider text-xs text-es-muted border-b border-es-soft">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dashboard.topSuppliers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-2 py-4 text-es-muted text-center">
                      No spend for these filters.
                    </td>
                  </tr>
                ) : (
                  dashboard.topSuppliers.map((row) => (
                    <tr key={row.parentName} className="border-b border-es-soft/60">
                      <td className="px-2 py-2">
                        <button
                          type="button"
                          className="font-medium text-es-navy hover:text-es-signal hover:underline text-left"
                          onClick={() => onSupplier(row.parentName)}
                        >
                          {row.parentName}
                        </button>
                      </td>
                      <td className="px-2 py-2 tabular-nums">{formatSpend(row.spend)}</td>
                      <td className="px-2 py-2 tabular-nums">{formatRatio(row.share)}</td>
                      <td className="px-2 py-2">
                        <EsBadge tone={riskBadgeTone(row.band)} className="!text-xs !px-2 !py-0.5">
                          {bandLabel(row.band)}
                        </EsBadge>
                      </td>
                      <td className="px-2 py-2 text-es-body">{row.priorityLine || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      ) : null}

      {sheet === 'po' ? (
      <Section
        title="PO and budget utilisation"
        note="Commitment comes from the PO extract. Utilization uses that extract’s invoiced amount."
      >
        {views?.po.hasPo ? (
          <>
            <UtilizationMeter
              title="PO utilization"
              ratio={views.po.utilization}
              fillLabel="Executive invoice"
              fillValue={dashboard.totalSpend}
              trackLabel="Commitment"
              trackValue={views.po.commitment}
              note={`Utilization is the PO extract’s invoiced amount, ${formatSpend(views.po.poInvoice)}, divided by commitment. The executive invoice total is the invoice cube.`}
            />
            <DataTable
              title="Largest remaining PO commitments"
              headers={['Supplier', 'PO value', 'Invoiced', 'Remaining', 'Utilization']}
              rows={views.po.headroom.map((row) => [
                cell(row.parentName),
                formatSpend(row.poValue),
                formatSpend(row.invoiced),
                formatSpend(row.remaining),
                formatRatio(row.utilization),
              ])}
              canOpenInCube={(index) => {
                const name = views.po.headroom[index]?.parentName ?? ''
                return name.length > 0 && invoiceParents.has(name)
              }}
              onOpenInCube={(index) => {
                const name = views.po.headroom[index]?.parentName
                if (name) openInCube({ parent: name })
              }}
            />
          </>
        ) : (
          <p className="text-sm text-es-muted rounded-[var(--radius-md)] border border-es-soft bg-es-haze/60 px-3 py-2">
            PO commitment is not in this invoice extract. Upload the PO vs Invoice sheet to show commitment and remaining headroom.
          </p>
        )}
        {views?.budget.hasBudget ? (
          <>
            <UtilizationMeter
              title={views.budget.latestYear ? `${views.budget.latestYear} budget utilization` : 'Budget utilization'}
              ratio={views.budget.latestBudget > 0 ? views.budget.latestSpend / views.budget.latestBudget : null}
              fillLabel="Spend"
              fillValue={views.budget.latestSpend}
              trackLabel={views.budget.latestYear ? `${views.budget.latestYear} budget` : 'Latest-year budget'}
              trackValue={views.budget.latestBudget}
              note={`Total budget across the extract is ${formatSpend(views.budget.totalBudget)}.`}
            />
            <PairedColumns
              title="Budget utilisation"
              note="Spend incurred against the budget allocated for each quarter."
              aLabel="Spend"
              bLabel="Budget"
              aColor={PAIR_GRAY}
              bColor={PAIR_BLUE}
              rows={views.budget.quarters.map((q) => ({
                label: `${q.year} Q${q.quarter}`,
                a: q.spend,
                b: q.budget,
              }))}
            />
          </>
        ) : null}
      </Section>
      ) : null}

      {sheet === 'summary' ? (
      <Section title="Spend cube summary" note="Turn the cube to change the face. Filters apply to the heatmap and every table on this tab.">
          <SpendCubeFaceCard
            companyId={companyId}
            filters={summaryFilters}
            options={summary?.options}
            onFilters={setSummaryFilters}
            onRowDim={onCubeRowDim}
            onReset={() => {
              setSummaryFilters(emptySummaryFilters({ year: null, bu: '', rollup: '' }))
              setOpenSlicer(null)
            }}
            filtersOpen={filtersOpen}
            onToggleFilters={() => {
              setFiltersOpen((open) => !open)
              setOpenSlicer(null)
            }}
            filtersPanel={
              <div ref={slicerBarRef} className="flex flex-wrap gap-2 items-center">
                <Slicer
                  label="Invoice Year"
                  options={(summary?.options.years || []).map(String)}
                  selected={summaryFilters.years.map(String)}
                  onChange={(next) =>
                    setSummaryFilters((prev) => ({
                      ...prev,
                      years: next.map((value) => Number(value)).filter((value) => value > 0),
                    }))
                  }
                  open={openSlicer === 'years'}
                  onToggle={() => setOpenSlicer(openSlicer === 'years' ? null : 'years')}
                />
                {(
                  [
                    ['costCenters', 'Cost Center Code', summaryFilters.costCenters, true],
                    ['bus', 'Procurement BU', summaryFilters.bus, false],
                    ['statuses', 'Invoice - Status', summaryFilters.statuses, false],
                    ['departments', 'Dept Desc', summaryFilters.departments, true],
                    ['rollups', 'Roll Up 2', summaryFilters.rollups, false],
                    ['accountNames', 'Account Name', summaryFilters.accountNames, true],
                    ['accountRollups', 'Account Rollup', summaryFilters.accountRollups, true],
                    ['reportRollups', 'Report Rollup', summaryFilters.reportRollups, false],
                    ['contractors', 'Contractor/Consultant', summaryFilters.contractors, false],
                    ['parents', 'Standardized Supplier Name', summaryFilters.parents, true],
                    ['sapItems', 'SAP Item#', summaryFilters.sapItems, true],
                    ['materials', 'Material Group', summaryFilters.materials, false],
                    ['plants', 'Plant Location', summaryFilters.plants, false],
                  ] as const
                ).map(([key, label, selected, searchable]) => (
                  <Slicer
                    key={key}
                    label={label}
                    searchable={searchable}
                    options={summary?.options[key] || []}
                    selected={selected}
                    onChange={(next) => setSummaryFilters((prev) => ({ ...prev, [key]: next }))}
                    open={openSlicer === key}
                    onToggle={() => setOpenSlicer(openSlicer === key ? null : key)}
                  />
                ))}
              </div>
            }
          />
          {summaryError ? (
            <p className="text-sm text-red-800">{summaryError}</p>
          ) : null}
          {summaryLoading && !summary ? (
            <p className="text-sm text-es-muted">Loading summary...</p>
          ) : summary && !summary.hasData ? (
            <p className="text-sm text-es-muted">No spend file for this company yet.</p>
          ) : summary ? (
            <SummaryTables summary={summary} rowDim={cubeRowDim} />
          ) : null}
          {summaryLoading && summary ? <p className="text-xs text-es-muted">Updating...</p> : null}
        </Section>
      ) : null}
    </div>
  )
}
